import { Box, Typography, Button, Alert } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import * as api from '../api';
import balanceStepperIcon from '../assets/icons/document.svg';
import { Stepper } from '../components/Stepper';
import { UsePointsDialogContainer } from '../container/UsePointsDialogContainer';
import {
  formatAmountInCurrency,
  formatDecimal,
  withSelectedOrder,
  withTotalPayment,
} from '../utils';
import { BalanceList } from './BalanceList';
import { BalanceContext, BalancesItem } from '../context/BalanceContext';
import { EarlyPaymentCalculationTable } from '../components/EarlyPaymentCalculationTable';

export function BalancesPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const { balances, setBalances, balancesSelection, setBalancesSelection } =
    useContext(BalanceContext);

  const selectedBalances = withSelectedOrder(
    balances || [],
    balancesSelection || [],
  );
  useEffect(() => {
    const fetchBalances = async () => {
      const q = new URLSearchParams(location.search);
      const surname = q.get('surname');
      const pnr = q.get('PNR');
      const orderNo = q.get('orderNo');

      if (!surname) return;

      try {
        const resItems = await api.balances({
          lastName: surname,
          orderNumber: pnr ? pnr : orderNo ?? undefined,
        });

        const updatedBalances = await Promise.all(
          resItems.map(async (item) => {
            let toPay: api.ApiAmountWithCurrency = {
              amount: item.payment.balance.amount,
              currency: { id: 724, code: 'TRY', name: 'Turkish Lira' },
              currencyExchangeRate: 0,
            };

            if (toPay.amount > 0) {
              toPay = await api.currencyExchange(
                item.payment.balance.currency.code,
                'TRY',
                item.payment.balance.amount,
              );
            }

            return {
              ...item,
              payment: {
                ...item.payment,
                loyalty: { points: 0, fiat: 0 },
                received: {
                  amount:
                    (item.payment.total.amount || 0) -
                    (item.payment.balance.amount || 0),
                  currency: item.payment.balance.currency,
                },
                toPay: {
                  amount: toPay?.amount || 0,
                  currencyExchangeRate:
                    item.payment.balance.amount > 0
                      ? item.payment.balance.amount / (toPay?.amount || 1)
                      : 0,
                  currency: toPay.currency || null,
                },
              },
            };
          }),
        );

        setBalances(updatedBalances);
      } catch (e: any) {
        console.log('erro');
      }
    };

    fetchBalances();
  }, [location.search, setBalances, nav, t]);

  const anyZeroSelectedBalance = selectedBalances.some(
    (balance) =>
      balance.payment.toPay.amount === 0 &&
      balance.payment.loyalty.points === 0,
  );

  const totalPayment = withTotalPayment(selectedBalances);

  const [balanceItemToUsePoint, setBalanceItemToUsePoint] =
    useState<BalancesItem | null>(null);
  const [exceedAmountErrorText, setExceedAmountErrorText] = useState<string[]>(
    [],
  );

  const handleLoyaltyPointsUse = (
    it: BalancesItem,
    loyalty: {
      points: number;
      fiat: number;
      currency?: api.ApiCurrency;
      currencyExchangeRate?: number;
    },
  ) => {
    if (!it) return;
    const newBalances = balances?.map((item) => {
      if (item.orderNumber === it.orderNumber) {
        let toPayAmount =
          item.payment.balance.amount /
          (it.payment.toPay.currencyExchangeRate || 1);

        if (loyalty.fiat > 0) {
          toPayAmount =
            (item.payment.total.amount -
              (item.payment.received.amount + loyalty.fiat)) /
            (loyalty.currencyExchangeRate || 1);
        }

        return {
          ...item,
          payment: {
            ...item.payment,
            toPay: {
              ...item.payment.toPay,
              amount: parseFloat(toPayAmount.toFixed(2)),
              currency: item.payment.toPay.currency,
            },
            loyalty,
          },
        };
      } else return item;
    });
    setBalances(newBalances);
    // close dialog
    setBalanceItemToUsePoint(null);
  };

  const handlePaymentAmountChange = async (
    it: BalancesItem,
    amount: number,
  ) => {
    //currencyExchange (example =>  TRY to EUR)
    const balanceAmount = (it.payment.toPay.currencyExchangeRate || 1) * amount;

    const newBalances = balances?.map((item) => {
      if (item.orderNumber === it.orderNumber)
        return {
          ...item,
          payment: {
            ...it.payment,
            balance: { ...item.payment.balance, amount: balanceAmount },
            toPay: { ...it.payment.toPay, amount },
          },
        };
      else return item;
    });
    setBalances(newBalances);
  };

  const noOrdersSelected = balancesSelection?.length === 0;
  const zeroTotal = totalPayment.amount + totalPayment.loyaltyAmount === 0;
  const [exceedAmountExist, setExceedAmountExist] = useState(false);

  useEffect(() => {
    setExceedAmountExist(exceedAmountErrorText.length > 0);
  }, [exceedAmountErrorText]);

  useEffect(() => {
    const errorText: string[] = [];

    selectedBalances.forEach((balance) => {
      const remainingAmount = formatDecimal(
        balance.payment.total.amount - balance.payment.received.amount,
      );

      const maxAmount = formatDecimal(
        (remainingAmount - balance.payment.loyalty.fiat) /
          (balance.payment.toPay.currencyExchangeRate || 1),
      );

      const toPayAmount = formatDecimal(balance.payment.toPay.amount);

      if (maxAmount < toPayAmount) {
        errorText.push(
          t('balancesPage.maxAmountError', {
            orderNumber: balance.orderNumber,
            maxAmount: maxAmount.toFixed(2),
          }),
        );
      }
    });
    (balances ? balances?.length : 0) > 0 &&
      setExceedAmountErrorText(errorText);
  }, [selectedBalances, t]);

  return (
    <>
      {balances?.length === 0 && (
        <Box>
          <Typography
            sx={{
              textAlign: 'center',
              mt: 16,
              fontWeight: 500,
              fontSize: '24px',
              color: 'red',
            }}
          >
            {' '}
            {t('balancesPage.noDebit')}
          </Typography>
        </Box>
      )}

      {balances && balances.length > 0 && (
        <Box>
          {balanceItemToUsePoint && (
            <UsePointsDialogContainer
              salesIndividualId={balanceItemToUsePoint.salesIndividualId}
              reservedPoints={selectedBalances.map((it) => ({
                salesIndividualId: it.salesIndividualId,
                points: it.payment.loyalty.points,
              }))}
              onUse={(vals) =>
                handleLoyaltyPointsUse(balanceItemToUsePoint, vals)
              }
              onClose={() => setBalanceItemToUsePoint(null)}
              open={true}
            />
          )}

          <Box
            m={[2, 3, 3, 4]}
            sx={{
              border: ['none', '2px solid #eee'],
              borderRadius: '10px',
              p: ['8px', '12px', '24px', '24px'],
            }}
          >
            <Stepper
              step={1}
              total={2}
              label={t('balancesPage.stepLabel')}
              labelIcon={balanceStepperIcon}
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Typography color="secondary" sx={{ fontSize: ['12px', 14] }}>
                {t('balancesPage.body')}
              </Typography>
              {selectedBalances.length > 0 && (
                <Box
                  sx={{
                    alignItems: 'center',
                    display: ['flex', 'flex', 'flex', 'none'],
                    minWidth: 'auto',
                    gap: 2,
                  }}
                >
                  {totalPayment.loyaltyAmount > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        minWidth: '90px',
                      }}
                    >
                      <Typography color="primary">
                        {formatAmountInCurrency(
                          totalPayment.loyaltyAmount,
                          totalPayment.currency.code,
                        )}
                      </Typography>
                      <Typography variant="inherit" sx={{ fontSize: '9px' }}>
                        {t('balancesPage.totalLoyalityAmount')}
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: '90px',
                    }}
                  >
                    <Typography color="#BE9A02">
                      {formatAmountInCurrency(
                        totalPayment.amount,
                        totalPayment.currency.code,
                      )}
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '9px' }}>
                      {t('balancesPage.totalAmount')}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
            <BalanceList
              balances={balances || []}
              balancesSelection={balancesSelection || []}
              setBalancesSelection={setBalancesSelection}
              setBalanceItemToUsePoint={setBalanceItemToUsePoint}
              handlePaymentAmountChange={handlePaymentAmountChange}
              handleLoyaltyPointsRemove={(balance: BalancesItem) =>
                handleLoyaltyPointsUse(balance, { fiat: 0, points: 0 })
              }
            />
            {noOrdersSelected && (
              <Alert severity="error" variant="filled">
                {t('balancesPage.alertNoSelectedOrders')}
              </Alert>
            )}
            {exceedAmountErrorText.length > 0 && (
              <Alert severity="error" variant="filled">
                {exceedAmountErrorText.join(' ')}
              </Alert>
            )}
            {!noOrdersSelected && zeroTotal && (
              <Alert severity="error" variant="filled">
                {t('balancesPage.alertZeroAmount')}
              </Alert>
            )}
            <Box sx={{ display: 'flex', mt: 3, justifyContent: 'end' }}>
              <Button
                color="primary"
                variant="contained"
                sx={{ mr: 2 }}
                onClick={() => nav(-1)}
              >
                {t('payment.backBtn')}
              </Button>
              <Button
                color="secondary"
                variant={'contained'}
                onClick={() => nav('/payment')}
                disabled={
                  noOrdersSelected ||
                  zeroTotal ||
                  anyZeroSelectedBalance ||
                  exceedAmountExist
                }
              >
                {t('paymentPage.continueBtn')}
              </Button>
            </Box>
          </Box>
          {balances && balances.length > 0 && (
            <Box sx={{ flexShrink: 0, ml: 3 }}>
              {balances && balances.length > 0 && (
                <EarlyPaymentCalculationTable
                  salesIndividualId={balances[0].salesIndividualId}
                />
              )}
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
